/**
 * `/assets` — the brand-assets library (migration 195): every hosted brand
 * media file the studio cites from its asset host, plus the uploader.
 *
 * Reads + mutates entirely through the chokepoint (`dispatchAction` →
 * `POST /api/dispatch/:action`, as `human:staff`), so the page owns NO
 * bespoke endpoint (`api: []` in page-registry, like `/errors`):
 * `list_brand_assets` for the gallery, `create_brand_asset_upload` →
 * (browser PUT to the signed URL) → `finalize_brand_asset` for an upload,
 * `update_brand_asset` for caption / category, `delete_brand_asset` for
 * delete, and `finalize_brand_asset` again for "Re-check URL".
 *
 * The bytes go browser-direct to Supabase Storage with the EXACT headers the
 * mint action returns (`content-type` = the mime the action decided,
 * `cache-control: max-age=3600`). That PUT is the one thing here that is not
 * a dispatch, and it changes no canonical state: the row is created by the
 * mint and only `finalize_brand_asset` can flip it to live. The SPA never
 * touches `supabase.storage` directly — that would bypass the action layer.
 *
 * Rules the page enforces in copy: names are lowercase + immutable (to change
 * content, upload a new name); a URL is offered for copying ONLY when the
 * catalog has verified it answers with a real file; deleting breaks every
 * sent email that cites the URL and edge caches keep the bytes up to an hour.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { dispatchAction } from "../lib/dispatch";
import { fmtBytes } from "../lib/format-bytes";
import { studioTimeZone } from "../lib/studio-tz";

// ---------------------------------------------------------------------------
// Wire shapes (mirror the canonical actions' outputs).
// ---------------------------------------------------------------------------

type AssetSource = "supabase_storage" | "netlify_static";
type AssetStatus = "pending" | "live";
type UrlStatus = "verified" | "unverified";

interface AssetRow {
  readonly id: string;
  readonly name: string;
  readonly mime_type: string;
  readonly size_bytes: number | null;
  readonly caption: string | null;
  readonly category: string | null;
  readonly source: AssetSource;
  readonly status: AssetStatus;
  readonly storage_path: string | null;
  readonly upload_expires_at: string | null;
  readonly public_url: string | null;
  readonly public_url_verified_at: string | null;
  readonly uploaded_by: string | null;
  readonly created_at: string;
  readonly updated_at: string;
  readonly url_status: UrlStatus;
  readonly storage_url: string | null;
  readonly poster_url: string | null;
}

interface ListOutput {
  readonly assets: ReadonlyArray<AssetRow>;
  readonly count_returned: number;
  readonly limit: number;
}

interface CreateUploadOutput {
  readonly asset_id: string;
  readonly name: string;
  readonly mime_type: string;
  readonly status: AssetStatus;
  readonly signed_url: string | null;
  readonly token: string | null;
  readonly expires_at: string | null;
  readonly upload_headers: { readonly "content-type": string; readonly "cache-control": string } | null;
  readonly public_url: string;
  readonly url_status: UrlStatus;
  readonly reminted: boolean;
  readonly already_uploaded: boolean;
}

interface FinalizeOutput {
  readonly asset_id: string;
  readonly name: string;
  readonly status: "live";
  readonly public_url: string | null;
  readonly url_status: UrlStatus;
}

interface UpdateOutput {
  readonly updated: true;
  readonly asset_id: string;
  readonly fields_changed: ReadonlyArray<string>;
}

interface DeleteOutput {
  readonly deleted: true;
  readonly asset_id: string;
  readonly name: string;
  readonly source: AssetSource;
  readonly object_removed: boolean;
  readonly file_still_hosted: boolean;
}

// ---------------------------------------------------------------------------
// Constants mirrored from `brand-assets-shared.ts` — pre-flight hints only;
// the chokepoint's JSON Schema + the bucket allow-list are the enforcement.
// ---------------------------------------------------------------------------

const MAX_BYTES = 52_428_800;
const LARGE_WARN_BYTES = 20 * 1024 * 1024;
const AUTO_PREVIEW_MAX_BYTES = 5 * 1024 * 1024;
const NAME_RE = /^[a-z0-9][a-z0-9._-]{0,118}\.[a-z0-9]{1,8}$/;
const MIME_BY_EXT: Readonly<Record<string, string>> = {
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  gif: "image/gif",
  webp: "image/webp",
  avif: "image/avif",
  mp4: "video/mp4",
  webm: "video/webm",
  mov: "video/quicktime",
  pdf: "application/pdf",
};
const ACCEPT = ".png,.jpg,.jpeg,.gif,.webp,.avif,.mp4,.webm,.mov,.pdf";
const UNCATEGORISED = "Uncategorised";

// ---------------------------------------------------------------------------
// Small helpers.
// ---------------------------------------------------------------------------

function extOf(name: string): string {
  const i = name.lastIndexOf(".");
  return i === -1 ? "" : name.slice(i + 1).toLowerCase();
}

/** `My Photo (2).JPG` → `my-photo-2.jpg` — lowercase, safe characters, extension kept. */
function slugifyName(filename: string): string {
  const dot = filename.lastIndexOf(".");
  const rawStem = dot > 0 ? filename.slice(0, dot) : filename;
  const rawExt = dot > 0 ? filename.slice(dot + 1) : "";
  const stem = rawStem
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9._-]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^[-.]+|[-.]+$/g, "");
  const ext = rawExt.toLowerCase().replace(/[^a-z0-9]/g, "");
  return ext.length > 0 ? `${stem || "asset"}.${ext}` : stem || "asset";
}

function fmtDateTime(iso: string | null): string {
  if (iso === null) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("en-GB", {
    timeZone: studioTimeZone(),
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function fmtTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleTimeString("en-GB", { timeZone: studioTimeZone(), hour: "2-digit", minute: "2-digit" });
}

function describeXhrFailure(xhr: XMLHttpRequest): string {
  try {
    const body = JSON.parse(xhr.responseText) as { code?: unknown; message?: unknown };
    const code = typeof body.code === "string" ? body.code : "storage_error";
    const message = typeof body.message === "string" ? body.message : xhr.statusText;
    return `${code}: ${message} (HTTP ${xhr.status})`;
  } catch {
    return `HTTP ${xhr.status} ${xhr.statusText || "upload rejected"}`;
  }
}

/**
 * The browser-direct PUT. XHR rather than fetch for `upload.onprogress`. The
 * headers are exactly what the mint returned — never `file.type`.
 */
function putToSignedUrl(
  url: string,
  headers: Readonly<Record<string, string>>,
  file: File,
  onProgress: (pct: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url);
    for (const [k, v] of Object.entries(headers)) xhr.setRequestHeader(k, v);
    xhr.timeout = 10 * 60 * 1000;
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(describeXhrFailure(xhr)));
    };
    xhr.onerror = () => reject(new Error("network_error: the upload could not reach Supabase Storage"));
    xhr.ontimeout = () => reject(new Error("upload_timeout: the upload took longer than 10 minutes"));
    xhr.onabort = () => reject(new Error("upload_aborted"));
    xhr.send(file);
  });
}

type ItemState = "queued" | "blocked" | "minting" | "uploading" | "finalizing" | "done" | "error";

interface UploadItem {
  readonly key: string;
  readonly file: File;
  readonly name: string;
  readonly caption: string;
  readonly category: string;
  readonly sizeBytes: number;
  /** Re-mint a pending name whose link is still valid (the Retry path). */
  readonly force: boolean;
  readonly state: ItemState;
  readonly progress: number;
  readonly message: string | null;
}

let itemSeq = 0;

// ---------------------------------------------------------------------------
// The page.
// ---------------------------------------------------------------------------

export function Assets(): JSX.Element {
  const [assets, setAssets] = useState<ReadonlyArray<AssetRow> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [search, setSearch] = useState<string>("");
  const [categoryFilter, setCategoryFilter] = useState<string>("");
  const [queue, setQueue] = useState<ReadonlyArray<UploadItem>>([]);
  const [uploading, setUploading] = useState<boolean>(false);
  const [dragOver, setDragOver] = useState<boolean>(false);
  const [deleteTarget, setDeleteTarget] = useState<AssetRow | null>(null);
  const [deleting, setDeleting] = useState<boolean>(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [retryName, setRetryName] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const retryInputRef = useRef<HTMLInputElement | null>(null);
  const copyFlashRef = useRef<number | null>(null);

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    const r = await dispatchAction<Record<string, unknown>, ListOutput>("list_brand_assets", { status: "all" });
    if (r.ok) {
      setAssets(r.response.assets);
    } else {
      setError(`${r.message} (${r.code})`);
      setAssets(null);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const categories = useMemo(() => {
    const set = new Set<string>();
    for (const a of assets ?? []) if (a.category !== null) set.add(a.category);
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [assets]);

  const filtered = useMemo(() => {
    if (assets === null) return null;
    const q = search.trim().toLowerCase();
    return assets.filter((a) => {
      if (categoryFilter !== "" && (a.category ?? UNCATEGORISED) !== categoryFilter) return false;
      if (q.length === 0) return true;
      return (
        a.name.includes(q) ||
        (a.caption ?? "").toLowerCase().includes(q) ||
        (a.category ?? "").toLowerCase().includes(q) ||
        a.mime_type.includes(q)
      );
    });
  }, [assets, search, categoryFilter]);

  const groups = useMemo(() => {
    if (filtered === null) return [];
    const map = new Map<string, AssetRow[]>();
    for (const a of filtered) {
      const k = a.category ?? UNCATEGORISED;
      const arr = map.get(k);
      if (arr) arr.push(a);
      else map.set(k, [a]);
    }
    const keys = [...map.keys()].sort((a, b) => {
      if (a === UNCATEGORISED) return 1;
      if (b === UNCATEGORISED) return -1;
      return a.localeCompare(b);
    });
    return keys.map((k) => ({
      category: k,
      rows: (map.get(k) ?? []).slice().sort((x, y) => x.name.localeCompare(y.name)),
    }));
  }, [filtered]);

  const counts = useMemo(() => {
    const all = assets ?? [];
    return {
      total: all.length,
      live: all.filter((a) => a.status === "live").length,
      pending: all.filter((a) => a.status === "pending").length,
      unverified: all.filter((a) => a.status === "live" && a.url_status === "unverified").length,
    };
  }, [assets]);

  // -- queue ----------------------------------------------------------------

  const updateItem = useCallback((key: string, patch: Partial<UploadItem>): void => {
    setQueue((q) => q.map((it) => (it.key === key ? { ...it, ...patch } : it)));
  }, []);

  const addFiles = useCallback(
    (files: ReadonlyArray<File>, forceName: string | null = null): void => {
      const existingNames = new Set((assets ?? []).map((a) => a.name));
      const pendingNames = new Set((assets ?? []).filter((a) => a.status === "pending").map((a) => a.name));
      setQueue((current) => {
        const batchNames = new Set(current.map((it) => it.name));
        const next: UploadItem[] = [];
        for (const file of files) {
          itemSeq += 1;
          const key = `u${itemSeq}`;
          const name = forceName ?? slugifyName(file.name);
          let message: string | null = null;
          if (file.size === 0) {
            message = "Empty file (or a folder) — skipped";
          } else if (file.size > MAX_BYTES) {
            message = `Over the ${fmtBytes(MAX_BYTES)} cap`;
          } else if (!NAME_RE.test(name)) {
            message = "Name must be lowercase with an extension (a-z, 0-9, . _ -)";
          } else if (MIME_BY_EXT[extOf(name)] === undefined) {
            message = "Extension not accepted (png, jpg, gif, webp, avif, mp4, webm, mov, pdf)";
          } else if (batchNames.has(name)) {
            message = "Same name twice in this batch";
          } else if (forceName === null && existingNames.has(name) && !pendingNames.has(name)) {
            message = "A live asset already has this name — names are immutable; pick another";
          }
          batchNames.add(name);
          next.push({
            key,
            file,
            name,
            caption: "",
            category: "",
            sizeBytes: file.size,
            force: forceName !== null,
            state: message === null ? "queued" : "blocked",
            progress: 0,
            message,
          });
        }
        return [...current, ...next];
      });
    },
    [assets],
  );

  const revalidateItem = useCallback(
    (key: string, name: string): void => {
      const existingLive = new Set((assets ?? []).filter((a) => a.status === "live").map((a) => a.name));
      setQueue((q) =>
        q.map((it) => {
          if (it.key !== key) return it;
          let message: string | null = null;
          if (!NAME_RE.test(name)) message = "Name must be lowercase with an extension (a-z, 0-9, . _ -)";
          else if (MIME_BY_EXT[extOf(name)] === undefined) message = "Extension not accepted";
          else if (q.some((o) => o.key !== key && o.name === name)) message = "Same name twice in this batch";
          else if (!it.force && existingLive.has(name)) message = "A live asset already has this name";
          if (it.state === "done" || it.state === "error") return { ...it, name };
          return { ...it, name, message, state: message === null ? "queued" : "blocked" };
        }),
      );
    },
    [assets],
  );

  const uploadOne = useCallback(
    async (item: UploadItem): Promise<void> => {
      updateItem(item.key, { state: "minting", progress: 0, message: null });
      const input: Record<string, unknown> = { name: item.name, size_bytes: item.sizeBytes };
      if (item.caption.trim().length > 0) input["caption"] = item.caption.trim();
      if (item.category.trim().length > 0) input["category"] = item.category.trim();
      if (item.force) input["force"] = true;
      let minted: CreateUploadOutput;
      try {
        const r = await dispatchAction<Record<string, unknown>, CreateUploadOutput>("create_brand_asset_upload", input);
        if (!r.ok) {
          updateItem(item.key, { state: "error", message: `${r.message} (${r.code})` });
          return;
        }
        minted = r.response;
      } catch (e) {
        updateItem(item.key, { state: "error", message: `network: ${(e as Error).message}` });
        return;
      }
      if (minted.already_uploaded) {
        updateItem(item.key, { state: "done", progress: 100, message: "Already uploaded — finalized" });
        return;
      }
      if (minted.signed_url === null || minted.upload_headers === null) {
        updateItem(item.key, { state: "error", message: "No upload link returned" });
        return;
      }
      updateItem(item.key, { state: "uploading" });
      try {
        await putToSignedUrl(minted.signed_url, minted.upload_headers, item.file, (pct) =>
          updateItem(item.key, { progress: pct }),
        );
      } catch (e) {
        updateItem(item.key, { state: "error", message: (e as Error).message });
        return;
      }
      updateItem(item.key, { state: "finalizing", progress: 100 });
      // Finalize is idempotent on the row: a network blip after a 200 PUT is
      // retried once, never re-minted.
      for (let attempt = 0; attempt < 2; attempt += 1) {
        try {
          const f = await dispatchAction<Record<string, unknown>, FinalizeOutput>("finalize_brand_asset", {
            asset_id: minted.asset_id,
          });
          if (f.ok) {
            updateItem(item.key, {
              state: "done",
              message: f.response.url_status === "verified" ? "Live" : "Live — URL not verified yet",
            });
            return;
          }
          if (f.code !== "non_json_response" || attempt === 1) {
            updateItem(item.key, { state: "error", message: `${f.message} (${f.code})` });
            return;
          }
        } catch (e) {
          if (attempt === 1) {
            updateItem(item.key, { state: "error", message: `finalize failed: ${(e as Error).message}` });
            return;
          }
        }
        await new Promise((r) => setTimeout(r, 1000));
      }
    },
    [updateItem],
  );

  const uploadAll = useCallback(async (): Promise<void> => {
    const ready = queue.filter((it) => it.state === "queued");
    if (ready.length === 0) return;
    setUploading(true);
    for (const it of ready) {
      await uploadOne(it);
    }
    setUploading(false);
    await load();
  }, [queue, uploadOne, load]);

  const removeItem = useCallback((key: string): void => {
    setQueue((q) => q.filter((it) => it.key !== key));
  }, []);

  const clearFinished = useCallback((): void => {
    setQueue((q) => q.filter((it) => it.state !== "done" && it.state !== "blocked"));
  }, []);

  // -- card actions ---------------------------------------------------------

  const saveMeta = useCallback(
    async (asset: AssetRow, patch: { caption?: string | null; category?: string | null }): Promise<string | null> => {
      const r = await dispatchAction<Record<string, unknown>, UpdateOutput>("update_brand_asset", {
        asset_id: asset.id,
        patch,
      });
      if (!r.ok) return `${r.message} (${r.code})`;
      await load();
      return null;
    },
    [load],
  );

  const recheck = useCallback(
    async (asset: AssetRow): Promise<string | null> => {
      const r = await dispatchAction<Record<string, unknown>, FinalizeOutput>("finalize_brand_asset", {
        asset_id: asset.id,
      });
      if (!r.ok) return `${r.message} (${r.code})`;
      await load();
      return r.response.url_status === "verified" ? null : "Still unverified — the host does not answer for this name yet";
    },
    [load],
  );

  const copyUrl = useCallback((asset: AssetRow): void => {
    if (asset.public_url === null) return;
    const clip = navigator.clipboard;
    if (clip === undefined || typeof clip.writeText !== "function") return;
    void clip.writeText(asset.public_url).then(
      () => {
        setCopiedId(asset.id);
        if (copyFlashRef.current !== null) window.clearTimeout(copyFlashRef.current);
        copyFlashRef.current = window.setTimeout(() => {
          setCopiedId(null);
          copyFlashRef.current = null;
        }, 1500);
      },
      () => setCopiedId(null),
    );
  }, []);

  const confirmDelete = useCallback(async (): Promise<void> => {
    if (deleteTarget === null) return;
    setDeleting(true);
    const r = await dispatchAction<Record<string, unknown>, DeleteOutput>("delete_brand_asset", {
      asset_id: deleteTarget.id,
    });
    setDeleting(false);
    if (!r.ok) {
      setError(`${r.message} (${r.code})`);
    }
    setDeleteTarget(null);
    await load();
  }, [deleteTarget, load]);

  const startRetry = useCallback((asset: AssetRow): void => {
    setRetryName(asset.name);
    window.setTimeout(() => retryInputRef.current?.click(), 0);
  }, []);

  // -- drop zone ------------------------------------------------------------

  const onDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>): void => {
      e.preventDefault();
      setDragOver(false);
      const files = Array.from(e.dataTransfer.files ?? []);
      if (files.length > 0) addFiles(files);
    },
    [addFiles],
  );

  const queuedCount = queue.filter((it) => it.state === "queued").length;

  return (
    <div className="page assets-page">
      <header className="page-header assets-header">
        <div>
          <h1 className="page-title">Brand assets</h1>
          <p className="assets-sub">
            Every image, clip, poster frame, logo and document the studio cites from its asset host.
            Copy a URL only when it shows <strong>verified</strong>. Names are lowercase and immutable —
            to change a file, upload it under a new name.
          </p>
        </div>
        <div className="assets-counts" aria-label="Catalog counts">
          <span className="tag">{counts.live} live</span>
          {counts.pending > 0 && <span className="tag assets-tag-warn">{counts.pending} pending</span>}
          {counts.unverified > 0 && <span className="tag assets-tag-warn">{counts.unverified} unverified</span>}
        </div>
      </header>

      <div className="assets-toolbar">
        <input
          type="search"
          className="assets-search"
          placeholder="Search name, caption, category…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search assets"
        />
        <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} aria-label="Filter by category">
          <option value="">All categories</option>
          {categories.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
          {(assets ?? []).some((a) => a.category === null) && <option value={UNCATEGORISED}>{UNCATEGORISED}</option>}
        </select>
        <button type="button" onClick={() => void load()} disabled={loading}>
          {loading ? "Loading…" : "Refresh"}
        </button>
        <label className="action-dispatch-btn assets-upload-btn">
          Upload files
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={ACCEPT}
            style={{ display: "none" }}
            onChange={(e) => {
              addFiles(Array.from(e.target.files ?? []));
              e.target.value = "";
            }}
          />
        </label>
        {/* Retry input for a pending row: the same name, forced re-mint. */}
        <input
          ref={retryInputRef}
          type="file"
          accept={ACCEPT}
          style={{ display: "none" }}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f !== undefined && retryName !== null) addFiles([f], retryName);
            setRetryName(null);
            e.target.value = "";
          }}
        />
      </div>

      <div
        className={`assets-drop${dragOver ? " assets-drop-over" : ""}`}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
      >
        Drop files here, or use <em>Upload files</em>. Up to {fmtBytes(MAX_BYTES)} each; png, jpg, gif, webp, avif,
        mp4, webm, mov, pdf. Files over {fmtBytes(LARGE_WARN_BYTES)} load slowly through the asset host — fine for a web
        page, not for an email.
      </div>

      {queue.length > 0 && (
        <section className="panel assets-queue">
          <h2>Upload queue</h2>
          <table className="data-table">
            <thead>
              <tr>
                <th>File</th>
                <th>Name (lowercase, immutable)</th>
                <th>Caption</th>
                <th>Category</th>
                <th>Size</th>
                <th>State</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {queue.map((it) => {
                const editable = it.state === "queued" || it.state === "blocked";
                return (
                  <tr key={it.key} className={`assets-queue-${it.state}`}>
                    <td className="assets-mono">{it.file.name}</td>
                    <td>
                      <input
                        type="text"
                        className="assets-mono"
                        value={it.name}
                        disabled={!editable || it.force}
                        onChange={(e) => revalidateItem(it.key, e.target.value.toLowerCase())}
                        aria-label={`Name for ${it.file.name}`}
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        value={it.caption}
                        maxLength={500}
                        disabled={!editable}
                        placeholder="Where it is used…"
                        onChange={(e) => updateItem(it.key, { caption: e.target.value })}
                        aria-label={`Caption for ${it.name}`}
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        list="assets-categories"
                        value={it.category}
                        maxLength={60}
                        disabled={!editable}
                        placeholder="e.g. Workout GIFs"
                        onChange={(e) => updateItem(it.key, { category: e.target.value })}
                        aria-label={`Category for ${it.name}`}
                      />
                    </td>
                    <td>
                      {fmtBytes(it.sizeBytes)}
                      {it.sizeBytes > LARGE_WARN_BYTES && <span className="assets-tag-warn tag">large</span>}
                    </td>
                    <td>
                      {it.state === "uploading" ? (
                        <span className="assets-progress" aria-label={`Uploading ${it.progress}%`}>
                          <span className="assets-progress-bar" style={{ width: `${it.progress}%` }} />
                          <span className="assets-progress-label">{it.progress}%</span>
                        </span>
                      ) : (
                        <span className={`tag assets-state-${it.state}`}>{it.state}</span>
                      )}
                      {it.message !== null && <div className="assets-item-msg">{it.message}</div>}
                    </td>
                    <td>
                      {(editable || it.state === "error" || it.state === "done") && (
                        <button type="button" onClick={() => removeItem(it.key)} aria-label={`Remove ${it.name} from the queue`}>
                          ×
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div className="button-row">
            <button type="button" className="action-dispatch-btn" onClick={() => void uploadAll()} disabled={uploading || queuedCount === 0}>
              {uploading ? "Uploading…" : `Upload ${queuedCount} file${queuedCount === 1 ? "" : "s"}`}
            </button>
            <button type="button" onClick={clearFinished} disabled={uploading}>
              Clear finished
            </button>
          </div>
        </section>
      )}

      <datalist id="assets-categories">
        {categories.map((c) => (
          <option key={c} value={c} />
        ))}
      </datalist>

      {error !== null && <div className="error-block">{error}</div>}

      {filtered !== null && filtered.length === 0 && !loading && (
        <p className="empty-state">
          {assets !== null && assets.length === 0
            ? "No assets catalogued yet — upload the first, or register the hosted files from the CLI."
            : "Nothing matches this filter."}
        </p>
      )}

      {groups.map((g) => (
        <section className="assets-group" key={g.category}>
          <h2 className="assets-group-title">
            {g.category} <span className="muted">({g.rows.length})</span>
          </h2>
          <div className="assets-grid">
            {g.rows.map((a) => (
              <AssetCard
                key={a.id}
                asset={a}
                copied={copiedId === a.id}
                onCopy={copyUrl}
                onSaveMeta={saveMeta}
                onRecheck={recheck}
                onDelete={setDeleteTarget}
                onRetry={startRetry}
              />
            ))}
          </div>
        </section>
      ))}

      {deleteTarget !== null && (
        <DeleteDialog
          asset={deleteTarget}
          busy={deleting}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={() => void confirmDelete()}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// One card.
// ---------------------------------------------------------------------------

interface AssetCardProps {
  readonly asset: AssetRow;
  readonly copied: boolean;
  readonly onCopy: (asset: AssetRow) => void;
  readonly onSaveMeta: (asset: AssetRow, patch: { caption?: string | null; category?: string | null }) => Promise<string | null>;
  readonly onRecheck: (asset: AssetRow) => Promise<string | null>;
  readonly onDelete: (asset: AssetRow) => void;
  readonly onRetry: (asset: AssetRow) => void;
}

function AssetCard({ asset, copied, onCopy, onSaveMeta, onRecheck, onDelete, onRetry }: AssetCardProps): JSX.Element {
  const [caption, setCaption] = useState<string>(asset.caption ?? "");
  const [category, setCategory] = useState<string>(asset.category ?? "");
  const [note, setNote] = useState<string | null>(null);
  const [busy, setBusy] = useState<boolean>(false);

  useEffect(() => {
    setCaption(asset.caption ?? "");
    setCategory(asset.category ?? "");
  }, [asset.caption, asset.category]);

  const commit = async (field: "caption" | "category"): Promise<void> => {
    const value = field === "caption" ? caption.trim() : category.trim();
    const current = (field === "caption" ? asset.caption : asset.category) ?? "";
    if (value === current) return;
    setBusy(true);
    const err = await onSaveMeta(asset, { [field]: value.length === 0 ? null : value });
    setBusy(false);
    setNote(err);
  };

  const pendingExpired =
    asset.status === "pending" &&
    (asset.upload_expires_at === null || new Date(asset.upload_expires_at).getTime() <= Date.now());

  return (
    <article className={`asset-card asset-card-${asset.status}`}>
      <Preview asset={asset} />
      <div className="asset-meta">
        <div className="asset-name assets-mono" title={asset.name}>
          {asset.name}
        </div>
        <div className="asset-facts">
          <span>{asset.size_bytes !== null ? fmtBytes(asset.size_bytes) : "—"}</span>
          <span>{asset.mime_type}</span>
          {asset.source === "netlify_static" && (
            <span className="tag" title="Deployed on the Netlify site itself; registered here so the gallery is complete">
              static
            </span>
          )}
          {asset.status === "pending" && <span className="tag assets-tag-warn">pending</span>}
        </div>

        {asset.status === "pending" ? (
          <div className="asset-pending">
            {pendingExpired
              ? "Upload incomplete — the link expired."
              : `Upload in progress — link expires ${asset.upload_expires_at !== null ? fmtTime(asset.upload_expires_at) : ""}.`}
            <div className="button-row">
              <button type="button" onClick={() => onRetry(asset)}>
                {pendingExpired ? "Choose file to retry" : "Choose file to replace link"}
              </button>
              <button type="button" onClick={() => onDelete(asset)}>
                Delete
              </button>
            </div>
          </div>
        ) : (
          <>
            <label className="asset-field">
              <span>Caption</span>
              <input
                type="text"
                value={caption}
                maxLength={500}
                disabled={busy}
                placeholder="Where it is used…"
                onChange={(e) => setCaption(e.target.value)}
                onBlur={() => void commit("caption")}
                onKeyDown={(e) => {
                  if (e.key === "Enter") (e.target as HTMLInputElement).blur();
                }}
              />
            </label>
            <label className="asset-field">
              <span>Category</span>
              <input
                type="text"
                list="assets-categories"
                value={category}
                maxLength={60}
                disabled={busy}
                placeholder="e.g. Workout GIFs"
                onChange={(e) => setCategory(e.target.value)}
                onBlur={() => void commit("category")}
                onKeyDown={(e) => {
                  if (e.key === "Enter") (e.target as HTMLInputElement).blur();
                }}
              />
            </label>

            {asset.url_status === "verified" && asset.public_url !== null ? (
              <div className="asset-url">
                <a href={asset.public_url} target="_blank" rel="noopener noreferrer" className="assets-mono">
                  {asset.public_url}
                </a>
                <button type="button" className="action-dispatch-btn" onClick={() => onCopy(asset)}>
                  {copied ? "Copied ✓" : "Copy URL"}
                </button>
              </div>
            ) : (
              <div className="asset-url asset-url-unverified">
                <div>
                  <strong>URL not verified</strong> — the asset host does not answer for this name yet (the proxy rule may
                  not be live). Do not cite it until it verifies.
                  {asset.public_url !== null && <div className="assets-mono muted">{asset.public_url}</div>}
                </div>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => {
                    setBusy(true);
                    void onRecheck(asset).then((msg) => {
                      setNote(msg);
                      setBusy(false);
                    });
                  }}
                >
                  Re-check URL
                </button>
              </div>
            )}

            {asset.poster_url !== null && (
              <div className="asset-poster muted">
                Poster frame: <span className="assets-mono">{asset.poster_url}</span>
              </div>
            )}

            <div className="asset-foot">
              <span className="muted" title={asset.uploaded_by ?? ""}>
                {fmtDateTime(asset.created_at)}
              </span>
              <button type="button" className="asset-delete" onClick={() => onDelete(asset)}>
                Delete
              </button>
            </div>
          </>
        )}
        {note !== null && <div className="assets-item-msg">{note}</div>}
      </div>
    </article>
  );
}

// ---------------------------------------------------------------------------
// Preview — poster-first for animated GIFs, metadata-only video, nothing
// above 5 MB auto-loads, and never the proxied URL for video (the direct
// object URL or the static URL is Range-capable).
// ---------------------------------------------------------------------------

function Preview({ asset }: { readonly asset: AssetRow }): JSX.Element {
  const [play, setPlay] = useState<boolean>(false);
  const [loadBig, setLoadBig] = useState<boolean>(false);
  const src = asset.storage_url ?? asset.public_url;
  const ext = extOf(asset.name);
  const isImage = asset.mime_type.startsWith("image/");
  const isVideo = asset.mime_type.startsWith("video/");
  const big = (asset.size_bytes ?? 0) > AUTO_PREVIEW_MAX_BYTES;

  if (asset.status === "pending" || src === null) {
    return <div className="asset-preview asset-preview-empty">{ext.toUpperCase() || "FILE"}</div>;
  }
  if (isImage) {
    const isGif = ext === "gif";
    if (isGif && asset.poster_url !== null && !play) {
      return (
        <button type="button" className="asset-preview asset-preview-btn" onClick={() => setPlay(true)} aria-label={`Play ${asset.name}`}>
          <img src={asset.poster_url} alt={asset.caption ?? asset.name} loading="lazy" decoding="async" />
          <span className="asset-play">▶ Play</span>
        </button>
      );
    }
    if (big && !loadBig && !play) {
      return (
        <button type="button" className="asset-preview asset-preview-btn" onClick={() => setLoadBig(true)}>
          <span className="asset-play">Load preview ({fmtBytes(asset.size_bytes ?? 0)})</span>
        </button>
      );
    }
    return (
      <div className="asset-preview">
        <img src={src} alt={asset.caption ?? asset.name} loading="lazy" decoding="async" />
      </div>
    );
  }
  if (isVideo) {
    return (
      <div className="asset-preview">
        <video src={src} preload="metadata" muted playsInline controls aria-label={asset.caption ?? asset.name} />
      </div>
    );
  }
  return (
    <a className="asset-preview asset-preview-empty" href={src} target="_blank" rel="noopener noreferrer">
      {ext.toUpperCase() || "FILE"}
    </a>
  );
}

// ---------------------------------------------------------------------------
// Typed-name delete confirmation.
// ---------------------------------------------------------------------------

interface DeleteDialogProps {
  readonly asset: AssetRow;
  readonly busy: boolean;
  readonly onCancel: () => void;
  readonly onConfirm: () => void;
}

function DeleteDialog({ asset, busy, onCancel, onConfirm }: DeleteDialogProps): JSX.Element {
  const [typed, setTyped] = useState<string>("");
  const inputRef = useRef<HTMLInputElement | null>(null);
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const match = typed.trim() === asset.name;

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>): void => {
    if (e.key === "Escape") {
      e.preventDefault();
      onCancel();
      return;
    }
    if (e.key === "Tab" && dialogRef.current !== null) {
      const focusables = dialogRef.current.querySelectorAll<HTMLElement>("input, button:not([disabled])");
      if (focusables.length === 0) return;
      const first = focusables[0]!;
      const last = focusables[focusables.length - 1]!;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  };

  return (
    <div className="assets-dialog-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onCancel()}>
      <div
        ref={dialogRef}
        className="assets-dialog"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="assets-delete-title"
        aria-describedby="assets-delete-desc"
        onKeyDown={onKeyDown}
      >
        <h2 id="assets-delete-title">Delete {asset.name}?</h2>
        <p id="assets-delete-desc">
          Every email, newsletter or page that cites this URL will show a broken image, and edge caches may keep serving
          the old bytes for up to an hour.
          {asset.source === "netlify_static" && (
            <>
              {" "}
              This is a <strong>static</strong> deployed on the site itself: only the catalog row is removed; the file
              stays hosted until the next site deploy.
            </>
          )}
        </p>
        <label htmlFor="assets-delete-input">
          Type the name to confirm: <span className="assets-mono">{asset.name}</span>
        </label>
        <input
          id="assets-delete-input"
          ref={inputRef}
          type="text"
          className="assets-mono"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          autoComplete="off"
          spellCheck={false}
        />
        <div className="button-row">
          <button type="button" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button type="button" className="action-dispatch-btn asset-delete" onClick={onConfirm} disabled={!match || busy}>
            {busy ? "Deleting…" : "Delete"}
          </button>
        </div>
      </div>
    </div>
  );
}
